/**
 * Everything the screens need before anyone has typed a word: the admin-editable copy, the
 * work interests, how many questions to expect, the host's four videos, the Cloudflare
 * Stream subdomain those videos play from, and this client's branding — their logo, their two
 * colours and the name and title of the person on camera (`GET /api/config`).
 *
 * Loaded once per page load and shared by every view. It also carries the app-wide fatal
 * flag, because a page that cannot boot has to say so instead of showing a white screen.
 */

import { computed, ref, type ComputedRef, type Ref } from 'vue';
import { defineStore } from 'pinia';
import { DEFAULT_SETTINGS } from '@altamont/shared';
import type {
  PublicAppSettings,
  PublicConfigResponse,
  PublicGlobalVideos,
  PublicInterest,
} from '@altamont/shared';

import { fetchConfig, friendlyErrorMessage } from '../lib/api';

/** Query parameters for the Cloudflare Stream iframe player. */
export interface StreamUrlOptions {
  /** Start playing as soon as the player is ready. Browsers only honour this when muted. */
  autoplay?: boolean;
  /** Mute the sound. The listening video is always muted (START.md §10). */
  muted?: boolean;
  /** Loop for ever — the listening video does, nothing else should. */
  loop?: boolean;
  /** Show the player's own controls. Defaults to `true`. */
  controls?: boolean;
  /** How much to fetch before playback. */
  preload?: 'none' | 'metadata' | 'auto';
  /** Start this many seconds in. */
  startTime?: number;
  /** Poster image URL shown before playback. */
  poster?: string;
  /** Turn captions on by default, e.g. `'en'` (accessibility, START.md §9). */
  defaultTextTrack?: string;
}

/** Query parameters for a Cloudflare Stream thumbnail. */
export interface StreamThumbnailOptions {
  /** Frame to grab, in seconds. */
  time?: number;
  /** Pixel height of the image. */
  height?: number;
}

/**
 * `GET /api/config` as the candidate Pages Function actually answers it: everything in the shared
 * `PublicConfigResponse`, plus the one field the server derives rather than stores.
 *
 * `settings.logoPath` names a Firebase Storage object the browser has no business fetching
 * directly, so the server hands back `logoUrl` — the address of `GET /api/branding/logo`, or
 * `null` when this client has not uploaded a logo. It is optional here because it is a server
 * addition to the shared contract: a payload without it is a client with no logo, not a broken
 * response.
 */
interface CandidateConfigResponse extends PublicConfigResponse {
  logoUrl?: string | null;
}

/**
 * What to call the person on camera while the settings are still on their way, or when an admin
 * has emptied the field. The same wording `DEFAULT_SETTINGS.hostName` seeds, so a candidate never
 * reads a gap where a name should be.
 */
const FALLBACK_HOST_NAME = 'Your host';

const EMPTY_GLOBAL_VIDEOS: PublicGlobalVideos = {
  welcome: null,
  intro: null,
  listening: null,
  closing: null,
};

/**
 * The player's accent colour, read off the document root rather than written out again, so the
 * one place `--alt-orange` is defined stays the only place. `BrandProvider` writes this client's
 * accent onto the same custom property, so the Stream player picks up their brand for free.
 */
function brandAccent(): string | null {
  if (typeof window === 'undefined' || typeof document === 'undefined') return null;
  const value = getComputedStyle(document.documentElement).getPropertyValue('--alt-orange').trim();
  return value || null;
}

export const useConfigStore = defineStore('config', () => {
  /** The whole `GET /api/config` payload, or `null` before the first successful load. */
  const config: Ref<CandidateConfigResponse | null> = ref(null);
  /** True while a load is in flight. */
  const loading = ref(false);
  /** Friendly message for a failed load, or `null`. */
  const error: Ref<string | null> = ref(null);
  /**
   * Set when the app cannot run at all (no Firebase config, an unhandled error during boot).
   * `App.vue` shows a plain apology and a way to reach a person instead of a broken flow.
   */
  const fatalError: Ref<string | null> = ref(null);

  let inFlight: Promise<void> | null = null;

  /** True once the config has been fetched successfully at least once. */
  const loaded = computed(() => config.value !== null);

  /** Admin-editable copy for every screen. `null` until loaded. */
  const settings: ComputedRef<PublicAppSettings | null> = computed(
    () => config.value?.settings ?? null,
  );

  /** Active work-interest categories, in the order HR put them in. */
  const interests: ComputedRef<PublicInterest[]> = computed(() => config.value?.interests ?? []);

  /** How many questions to expect, for the welcome screen. The frozen snapshot is what counts later. */
  const questionCount = computed(() => config.value?.questionCount ?? 0);

  /** The host's welcome, introduction, listening and closing videos. */
  const globalVideos: ComputedRef<PublicGlobalVideos> = computed(
    () => config.value?.globalVideos ?? EMPTY_GLOBAL_VIDEOS,
  );

  /** Cloudflare Stream customer subdomain, e.g. `customer-xxxx.cloudflarestream.com`. */
  const customerSubdomain = computed(() => config.value?.customerSubdomain ?? '');

  /**
   * What this client calls the person on camera (`hostName` in the settings). Every screen reads
   * the name from here rather than writing one into its copy, so one client's host can be their
   * HR director and another's a plant manager without a line of code changing.
   */
  const hostName = computed(() => (settings.value?.hostName ?? '').trim() || FALLBACK_HOST_NAME);

  /**
   * What this client calls that person's job (`hostTitle`). Empty when nobody has filled it in —
   * a name on its own reads perfectly well, and an invented title would be a small lie.
   */
  const hostTitle = computed(() => (settings.value?.hostTitle ?? '').trim());

  /**
   * Where to fetch this client's logo, or `null` when they have not uploaded one. A relative path
   * on our own origin, never a third-party URL, so no candidate's browser is sent elsewhere.
   */
  const logoUrl: ComputedRef<string | null> = computed(() => {
    const value = config.value?.logoUrl;
    if (typeof value !== 'string') return null;
    const trimmed = value.trim();
    return trimmed === '' ? null : trimmed;
  });

  /**
   * The two colours this client picked. An empty field falls back to the design system's own
   * navy and orange, so a half-filled settings document still paints a finished-looking app.
   * These are the raw stored values; `BrandProvider` is what turns them into a readable palette.
   */
  const brandPrimaryColor = computed(
    () => (settings.value?.brandPrimaryColor ?? '').trim() || DEFAULT_SETTINGS.brandPrimaryColor,
  );

  const brandAccentColor = computed(
    () => (settings.value?.brandAccentColor ?? '').trim() || DEFAULT_SETTINGS.brandAccentColor,
  );

  /**
   * Is Altamont accepting video applications? Assumed `true` until we know otherwise, so a
   * slow config never bounces someone to the closed screen by accident.
   */
  const isEnabled = computed(() => settings.value?.applicationEnabled !== false);

  /** The message HR wrote for the closed screen. */
  const disabledMessage = computed(() => settings.value?.disabledMessage ?? '');

  /**
   * Fetch the config once. Concurrent callers share one request; later calls are no-ops
   * unless `force` is set. Never throws — read {@link error} instead.
   */
  async function load(force = false): Promise<void> {
    if (config.value && !force) return;
    if (inFlight) return inFlight;

    loading.value = true;
    error.value = null;
    inFlight = (async () => {
      try {
        config.value = await fetchConfig();
      } catch (cause) {
        console.error('[altamont] Could not load the application configuration.', cause);
        error.value = friendlyErrorMessage(cause);
      } finally {
        loading.value = false;
        inFlight = null;
      }
    })();
    return inFlight;
  }

  /** Put the whole app into its fatal state. Called from the boot sequence and the error handler. */
  function setFatal(message: string): void {
    fatalError.value = message;
  }

  /**
   * The Cloudflare Stream iframe URL for one of the host's videos. Returns `''` when the video
   * or the subdomain is missing, so a component can simply not render the frame.
   */
  function streamUrl(
    streamVideoId: string | null | undefined,
    options: StreamUrlOptions = {},
  ): string {
    const subdomain = customerSubdomain.value;
    if (!subdomain || !streamVideoId) return '';

    const url = new URL(`https://${subdomain}/${encodeURIComponent(streamVideoId)}/iframe`);
    const params = url.searchParams;
    if (options.autoplay) params.set('autoplay', 'true');
    if (options.muted) params.set('muted', 'true');
    if (options.loop) params.set('loop', 'true');
    if (options.controls === false) params.set('controls', 'false');
    if (options.preload) params.set('preload', options.preload);
    if (typeof options.startTime === 'number' && options.startTime > 0) {
      params.set('startTime', String(Math.floor(options.startTime)));
    }
    if (options.poster) params.set('poster', options.poster);
    if (options.defaultTextTrack) params.set('defaultTextTrack', options.defaultTextTrack);

    const accent = brandAccent();
    if (accent) params.set('primaryColor', accent);

    return url.toString();
  }

  /** A still frame from one of the host's videos, for use as a poster. `''` when unavailable. */
  function streamThumbnailUrl(
    streamVideoId: string | null | undefined,
    options: StreamThumbnailOptions = {},
  ): string {
    const subdomain = customerSubdomain.value;
    if (!subdomain || !streamVideoId) return '';

    const url = new URL(
      `https://${subdomain}/${encodeURIComponent(streamVideoId)}/thumbnails/thumbnail.jpg`,
    );
    if (typeof options.time === 'number' && options.time > 0) {
      url.searchParams.set('time', `${Math.floor(options.time)}s`);
    }
    if (typeof options.height === 'number' && options.height > 0) {
      url.searchParams.set('height', String(Math.floor(options.height)));
    }
    return url.toString();
  }

  return {
    config,
    loading,
    error,
    fatalError,
    loaded,
    settings,
    interests,
    questionCount,
    globalVideos,
    customerSubdomain,
    hostName,
    hostTitle,
    logoUrl,
    brandPrimaryColor,
    brandAccentColor,
    isEnabled,
    disabledMessage,
    load,
    setFatal,
    streamUrl,
    streamThumbnailUrl,
  };
});
